import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, XCircle, MinusCircle, ExternalLink } from 'lucide-react';
import { api } from '../../lib/api';
import { nyDateKey } from '../../lib/datetime';
import EmptyState from './EmptyState';

const STATUS_META = {
  success:  { icon: CheckCircle2, tone: 'text-emerald-300' },
  failure:  { icon: XCircle,      tone: 'text-red-300' },
  canceled: { icon: MinusCircle,  tone: 'text-gray-400' },
};

export default function DeployHistory({ projectId, limit = 20 }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sp = new URLSearchParams();
      if (projectId) sp.set('project_id', projectId);
      sp.set('limit', String(limit));
      const res = await api.get(`/api/admin/deploy-events?${sp.toString()}`);
      setEvents(res.deployEvents || []);
    } finally { setLoading(false); }
  }, [projectId, limit]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="animate-pulse h-16 rounded-lg bg-white/[0.03]" />;
  if (events.length === 0) return <EmptyState title="No deploy events" description="The deploy-state sync worker activates on deploy day." />;

  // Group by day for readability
  const groups = {};
  for (const e of events) {
    const day = nyDateKey(e.deployedAt);
    if (!groups[day]) groups[day] = [];
    groups[day].push(e);
  }

  return (
    <div className="space-y-4">
      {Object.entries(groups).map(([day, items]) => (
        <div key={day}>
          <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">{day}</div>
          <div className="space-y-1">
            {items.map((e) => {
              const m = STATUS_META[e.status] || STATUS_META.success;
              const Icon = m.icon;
              return (
                <div key={e.id} className="flex items-center gap-3 p-2 rounded-lg border border-white/[0.04] bg-white/[0.015] text-[12px]">
                  <Icon className={`w-3.5 h-3.5 shrink-0 ${m.tone}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-gray-300 truncate">
                      <span className="uppercase tracking-wider text-[10px] text-gray-500">{e.environment}</span> · {e.cfDeploymentId}
                    </div>
                    <div className="text-[11px] text-gray-600">{String(e.deployedAt).slice(11, 16)}</div>
                  </div>
                  {e.deployUrl && (
                    <a href={e.deployUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-[#B0FC38] hover:text-white">
                      open <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
